import { useEffect, useState, useCallback } from 'react';
import { ChartLineUp, Users, Ticket as TicketIcon, CurrencyEur, Globe, Pulse } from '@phosphor-icons/react';
import { API } from '@/App';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

const PILL_BASE = 'rounded-lg border border-[#cba135]/15 bg-[#1a1508] p-4 shadow-[0_8px_32px_rgba(0,0,0,0.6)]';
const LABEL_BASE = 'text-[10px] uppercase tracking-[0.2em] font-bold text-[#a8872b]';
const VALUE_BASE = 'heading-font text-2xl sm:text-3xl text-[#cba135] mt-1 tabular-nums';
const SUBVALUE_BASE = 'text-[11px] text-[#cba135]/55 mt-0.5';

function StatCard({ label, value, sub, icon: Icon, accent = false, testid }) {
  return (
    <div className={PILL_BASE} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={LABEL_BASE}>{label}</p>
          <p className={`${VALUE_BASE} ${accent ? 'text-[#e6be4b]' : ''} truncate`}>{value}</p>
          {sub && <p className={SUBVALUE_BASE}>{sub}</p>}
        </div>
        {Icon && (
          <div className={`shrink-0 ${accent ? 'text-[#e6be4b]' : 'text-[#cba135]/50'}`}>
            <Icon size={22} weight="duotone" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminAnalyticsPanel({ token }) {
  const [overview, setOverview] = useState(null);
  const [live, setLive] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        setLoadError(`HTTP ${r.status}${txt ? ` · ${txt.slice(0, 80)}` : ''}`);
        return;
      }
      const d = await r.json();
      setOverview(d);
      setLive(d.live);
      setLoadError(null);
    } catch (err) {
      setLoadError(err?.message || 'Network error');
    }
  }, [token]);

  const fetchLiveOnly = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/analytics/live`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setLive(await r.json());
    } catch {
      // ignore live-only refresh errors
    }
  }, [token]);

  useEffect(() => {
    fetchOverview();
    const tFull = setInterval(fetchOverview, 60000);  // full overview every 60s
    const tLive = setInterval(fetchLiveOnly, 10000);  // live counter every 10s
    return () => { clearInterval(tFull); clearInterval(tLive); };
  }, [fetchOverview, fetchLiveOnly]);

  if (loadError) {
    return (
      <div className={PILL_BASE} data-testid="analytics-error">
        <p className={LABEL_BASE}>Live Analytics</p>
        <p className="text-sm text-red-300/85 mt-2">Could not load analytics ({loadError}).</p>
      </div>
    );
  }
  if (!overview) {
    return (
      <div className={PILL_BASE}>
        <p className={LABEL_BASE}>Live Analytics</p>
        <p className="text-sm text-[#cba135]/60 mt-2">Loading…</p>
      </div>
    );
  }

  const activeCount = live?.activeCount ?? 0;
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const fmtCur = (n) => `€${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <section data-testid="admin-analytics-panel" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ChartLineUp size={26} weight="duotone" className="text-[#cba135]" />
          <h3 className="heading-font text-lg sm:text-xl font-medium text-[#cba135]">Live Analytics</h3>
        </div>
        {GA_ID && (
          <a
            href={`https://analytics.google.com/analytics/web/#/p${encodeURIComponent(GA_ID)}/reports/intelligenthome`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs uppercase tracking-widest text-[#cba135]/70 hover:text-[#cba135] underline decoration-[#cba135]/30 underline-offset-4"
            data-testid="ga-dashboard-link"
          >
            Open GA4 Dashboard →
          </a>
        )}
      </div>

      {/* Live + Tickets + Revenue + Users (row 1) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`${PILL_BASE} relative overflow-hidden`} data-testid="live-viewers-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={LABEL_BASE}>Viewers now</p>
              <p className={`${VALUE_BASE} text-[#e6be4b]`} data-testid="live-viewers-count">{fmt(activeCount)}</p>
              <p className={SUBVALUE_BASE}>Last 60 s</p>
            </div>
            <span className="relative shrink-0 flex h-3 w-3 mt-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
            </span>
          </div>
        </div>
        <StatCard
          label="Tickets · 24h"
          value={fmt(overview.tickets.last24h)}
          sub={`${fmt(overview.tickets.last7d)} last 7 days`}
          icon={TicketIcon}
          testid="tickets-24h-card"
        />
        <StatCard
          label="Revenue · 24h"
          value={fmtCur(overview.revenue.last24h)}
          sub={`${fmtCur(overview.revenue.last7d)} last 7 days`}
          icon={CurrencyEur}
          accent
          testid="revenue-24h-card"
        />
        <StatCard
          label="New users · 24h"
          value={fmt(overview.users.new24h)}
          sub={`${fmt(overview.users.new7d)} last 7 days`}
          icon={Users}
          testid="users-new-24h-card"
        />
      </div>

      {/* KYC funnel + total users (row 2) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Users total"
          value={fmt(overview.users.total)}
          sub={`${fmt(overview.users.active24h)} active 24 h`}
          icon={Users}
          testid="users-total-card"
        />
        <StatCard
          label="KYC pending"
          value={fmt(overview.kyc.pending)}
          icon={Pulse}
          testid="kyc-pending-card"
        />
        <StatCard
          label="KYC approved"
          value={fmt(overview.kyc.approved)}
          sub={`${fmt(overview.kyc.rejected)} rejected`}
          testid="kyc-approved-card"
        />
        <StatCard
          label="Tickets total"
          value={fmt(overview.tickets.total)}
          sub={`Reg→Ticket 24h: ${overview.conversion.registerToTicketLast24hPct}%`}
          icon={TicketIcon}
          testid="tickets-total-card"
        />
      </div>

      {/* Geo + Live by page */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Geo breakdown */}
        <div className={PILL_BASE} data-testid="geo-breakdown-card">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={18} weight="duotone" className="text-[#cba135]" />
            <p className={LABEL_BASE}>Top countries (registered users)</p>
          </div>
          {overview.users.byCountry.length === 0 ? (
            <p className="text-sm text-[#cba135]/50">No country data yet</p>
          ) : (
            <ul className="space-y-1.5">
              {overview.users.byCountry.map((c) => {
                const max = overview.users.byCountry[0]?.count || 1;
                const width = Math.max(4, Math.round((c.count / max) * 100));
                return (
                  <li key={c.country} className="flex items-center gap-3 text-sm" data-testid={`geo-row-${c.country}`}>
                    <span className="w-8 font-mono text-[#e6be4b] uppercase tracking-wider text-xs">{c.country}</span>
                    <div className="flex-1 h-1.5 bg-[#0e0b04] rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#cba135] to-[#a8872b]" style={{ width: `${width}%` }} />
                    </div>
                    <span className="w-10 text-right tabular-nums text-[#cba135]/80">{fmt(c.count)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Live by page */}
        <div className={PILL_BASE} data-testid="live-by-page-card">
          <div className="flex items-center gap-2 mb-3">
            <Pulse size={18} weight="duotone" className="text-[#cba135]" />
            <p className={LABEL_BASE}>Live · By page</p>
          </div>
          {!live || activeCount === 0 ? (
            <p className="text-sm text-[#cba135]/50">No active viewers</p>
          ) : (
            <ul className="space-y-1.5">
              {Object.entries(live.byPage || {})
                .sort((a, b) => b[1] - a[1])
                .map(([page, count]) => (
                  <li key={page} className="flex items-center justify-between text-sm" data-testid={`live-page-${page.replace(/[^a-z0-9]/gi, '_')}`}>
                    <span className="font-mono text-xs text-[#e6be4b] truncate max-w-[70%]">{page}</span>
                    <span className="tabular-nums text-[#cba135]/85">{fmt(count)}</span>
                  </li>
                ))}
            </ul>
          )}
          {GA_ID && (
            <p className="text-[10px] text-[#cba135]/40 mt-3 leading-relaxed">
              For page views, sessions and full funnel see GA4 dashboard above.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
